import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {CSSProperties, useCallback, useLayoutEffect, useRef} from 'react';
import {FixedSizeList, ListChildComponentProps} from 'react-window';

import AlignmentTick from 'search/blast/AlignmentTick';
import {HitSequence} from 'search/blast/alignmentTypes';
import {DragSelected} from 'search/blast/BLASTAlignmentSelectHelper';
import {getHitBaseAtIndex, listScrollTo, zoomToPairwiseItemSize} from 'search/blast/BLASTAlignmentUtil';
import {ColoringSchemeType, getResidueColorStyle} from 'search/blast/ColoringScheme';
import {AlignmentConstants} from 'search/blast/constants';
import PairwiseDragZoomScreen from 'search/blast/PairwiseDragZoomScreen';
import {Operator} from 'search/SearchConstants';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';

interface QueryColumnProps {
  index: number; // 0-based sequence position
  style: React.CSSProperties;
  width: number;
  query: string;
  coloringScheme: ColoringSchemeType;
  isCompact: boolean;
  leftEdge?: boolean;
  rightEdge?: boolean;
}

const QueryColumn: React.FC<QueryColumnProps> = ({
  index,
  style,
  width,
  query,
  coloringScheme,
  isCompact,
  leftEdge,
  rightEdge,
}) => (
  <div
    className={css(styles.container, isCompact ? styles.compactQueryColumn : styles.queryColumn)}
    style={{...style}}
  >
    <div className={css(isCompact ? styles.compactTickRow : styles.tickRow)}>
      {(index + 1) % 5 === 0 && <AlignmentTick tick={index + 1} isCompact={isCompact} />}
    </div>
    <div style={getResidueColorStyle(coloringScheme, query)}>{query}</div>
    <PairwiseDragZoomScreen totalLength={width} leftEdge={leftEdge} rightEdge={rightEdge} isQuery={true} />
  </div>
);

interface EmptySelectColumnProps {
  style: React.CSSProperties;
  width: number;
  graySelected?: boolean;
  orangeSelected?: boolean;
  leftEdge?: boolean;
  rightEdge?: boolean;
}

const EmptySelectColumn: React.FC<EmptySelectColumnProps> = ({
  style,
  width,
  graySelected,
  orangeSelected,
  leftEdge,
  rightEdge,
}) => (
  <div style={{...style}}>
    <PairwiseDragZoomScreen
      totalLength={width}
      graySelected={graySelected}
      orangeSelected={orangeSelected}
      leftEdge={leftEdge}
      rightEdge={rightEdge}
    />
  </div>
);

interface HitColumnProps {
  style: React.CSSProperties;
  query: string;
  width: number;
  hit: string;
  coloringScheme: ColoringSchemeType;
  graySelected?: boolean;
  orangeSelected?: boolean;
  leftEdge?: boolean;
  rightEdge?: boolean;
}

const HitColumn: React.FC<HitColumnProps> = ({
  style,
  query,
  width,
  hit,
  coloringScheme,
  graySelected,
  orangeSelected,
  leftEdge,
  rightEdge,
}) => (
  <div className={classNames(css(styles.container))} style={{...style}}>
    <div style={getResidueColorStyle(coloringScheme, hit, query !== hit)}>{hit}</div>
    <PairwiseDragZoomScreen
      totalLength={width}
      graySelected={graySelected}
      orangeSelected={orangeSelected}
      leftEdge={leftEdge}
      rightEdge={rightEdge}
    />
  </div>
);

export interface PairwiseAlignmentProps {
  scroll: number;
  zoom: number;
  handleDragStart: (e: React.MouseEvent<HTMLDivElement>) => void;
  querySequence: string;
  rowHeight: number;
  rowWidth: number;
  isCompact: boolean;
  hitSequence?: HitSequence;
  dragSelected?: DragSelected;
  blastOperator?: Operator;
  coloringScheme?: ColoringSchemeType;
}

export default function PairwiseAlignment({
  scroll,
  zoom,
  handleDragStart,
  querySequence,
  rowHeight,
  rowWidth,
  isCompact,
  hitSequence,
  dragSelected,
  blastOperator,
  coloringScheme = ColoringSchemeType.Mismatch,
}: PairwiseAlignmentProps): JSX.Element {
  const listRef = useRef<FixedSizeList>(null);

  useLayoutEffect(() => {
    listScrollTo(listRef, scroll);
  });

  const renderColumn = useCallback(
    ({index, style}: ListChildComponentProps): JSX.Element => {
      const selected =
        dragSelected &&
        index >= dragSelected.residueStart &&
        dragSelected.residueEnd != null &&
        index < dragSelected.residueEnd;
      const singleBar = dragSelected && index === dragSelected.residueStart;

      if (hitSequence === undefined) {
        return (
          <QueryColumn
            index={index}
            style={style}
            width={rowWidth}
            query={querySequence[index]}
            coloringScheme={blastOperator === Operator.BLASTN ? ColoringSchemeType.Mismatch : coloringScheme}
            isCompact={isCompact}
            leftEdge={singleBar}
            rightEdge={selected && index === dragSelected.residueEnd! - 1}
          />
        );
      }

      const hitBase = getHitBaseAtIndex(querySequence, hitSequence, index);

      if (hitBase !== ' ') {
        return (
          <HitColumn
            style={style}
            query={querySequence[index]}
            width={rowWidth}
            hit={hitBase}
            coloringScheme={blastOperator === Operator.BLASTN ? ColoringSchemeType.Mismatch : coloringScheme}
            graySelected={selected && !dragSelected.rowSelected}
            orangeSelected={selected && dragSelected.rowSelected}
            leftEdge={singleBar}
            rightEdge={selected && index === dragSelected.residueEnd! - 1}
          />
        );
      } else if (selected || singleBar) {
        return (
          <EmptySelectColumn
            style={style}
            width={rowWidth}
            graySelected={selected && !dragSelected.rowSelected}
            orangeSelected={selected && dragSelected.rowSelected}
            leftEdge={index === dragSelected.residueStart}
            rightEdge={index === dragSelected.residueEnd! - 1}
          />
        );
      } else {
        return <> </>;
      }
    },
    [blastOperator, coloringScheme, dragSelected, hitSequence, isCompact, querySequence, rowWidth]
  );

  const wholeCell: CSSProperties = {
    position: 'relative',
    height: `${rowHeight}px`,
  };

  return (
    <div
      {...getTestProps({component: 'PairwiseAlignment', element: 'root'})}
      style={wholeCell}
      onMouseDown={handleDragStart}
    >
      <FixedSizeList
        className={css(styles.noScrollbars)}
        ref={listRef}
        initialScrollOffset={scroll}
        height={rowHeight}
        itemCount={querySequence.length}
        itemSize={zoomToPairwiseItemSize(zoom)}
        layout="horizontal"
        width={rowWidth}
        overscanCount={10}
      >
        {renderColumn}
      </FixedSizeList>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    fontSize: '14px',
    fontFamily: FONT_FAMILY_MONOSPACED,
    textAlign: 'center',
  },
  queryColumn: {
    height: AlignmentConstants.columnHeight.normal,
    gap: AlignmentConstants.columnGap.normal,
  },
  compactQueryColumn: {
    height: AlignmentConstants.columnHeight.compact,
    gap: AlignmentConstants.columnGap.compact,
  },
  tickRow: {
    height: AlignmentConstants.tickHeight.normal,
  },
  compactTickRow: {
    height: AlignmentConstants.tickHeight.compact,
  },
  noScrollbars: {
    overflow: 'hidden',
    scrollbarWidth: 'thin',
    scrollbarColor: 'transparent transparent',
    '&::-webkit-scrollbar': {
      width: '1px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'transparent',
    },
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
});
